// Shared building blocks for the Coach Analytics-Deepdive (Phase 4e).
import { useCallback, useState } from "react";
import { api } from "@/lib/api";
import { useI18n, useT } from "@/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Sparkles, ChevronDown, ChevronUp, TrendingUp, TrendingDown, ChevronRight } from "lucide-react";

export function fmtEur(cents) {
  const sign = (cents || 0) < 0 ? "-" : "";
  return `${sign}€${(Math.abs(cents || 0) / 100).toFixed(2)}`;
}

export function fmtMonthSince(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-DE", { month: "short", year: "numeric" });
  } catch { return String(iso).slice(0, 7); }
}

// Authenticated file download — carries the Bearer token (reports are gated).
export async function downloadReport(path, filename, t) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return true;
  } catch (e) {
    const { toast } = await import("sonner");
    toast.error(t("an.t12_download_failed"));
    return false;
  }
}

export function SectionLabel({ children }) {
  return <p className="mt-4 mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{children}</p>;
}

export function EmptyLine({ children }) {
  return <p className="py-2 text-center text-sm italic text-muted-foreground">{children}</p>;
}

export function MiniStat({ icon: Icon, label, value, tone }) {
  const color = tone === "success" ? "text-emerald-600" : tone === "warning" ? "text-amber-600" : "text-foreground";
  return (
    <div className="flex-1 rounded-xl bg-secondary/60 p-3">
      {Icon && <Icon className={`h-4 w-4 ${color}`} />}
      <p className="mt-1 text-[11px] text-muted-foreground">{label}</p>
      <p className={`font-display text-lg font-semibold ${color}`}>{value}</p>
    </div>
  );
}

export function MiniGrid({ children }) {
  return <div className="mb-2 flex gap-2">{children}</div>;
}

// Horizontal labelled bar (emotions / sources / top items).
export function BarRow({ label, value, pct, tone, testId }) {
  const bg = tone === "success" ? "bg-emerald-500" : "bg-primary";
  return (
    <div className="mb-1.5 flex items-center gap-2" data-testid={testId}>
      <span className="w-28 shrink-0 truncate text-xs capitalize text-foreground">{label}</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-secondary">
        <div className={`h-full rounded-full ${bg}`} style={{ width: `${Math.max(3, Math.min(100, pct))}%` }} />
      </div>
      <span className="w-10 shrink-0 text-right text-xs font-medium text-foreground">{value}</span>
    </div>
  );
}

export function Chip({ label, count }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2.5 py-1 text-xs capitalize text-primary">
      {label}{count != null && <span className="font-semibold opacity-70">{count}</span>}
    </span>
  );
}

// Numbered tile card with an AI explanation footer.
export function Tile({ num, title, icon: Icon, period, children }) {
  return (
    <section className="rounded-2xl border border-border bg-card p-5" data-testid={`an-tile-${num}`}>
      <div className="mb-3 flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">{num}</span>
        {Icon && <Icon className="h-4 w-4 text-primary" />}
        <p className="text-sm font-semibold text-foreground">{title}</p>
      </div>
      <div>{children}</div>
      <AITip tile={num} period={period} />
    </section>
  );
}

export function TileSkeleton() {
  return <Skeleton className="h-16 rounded-xl" />;
}

function AITip({ tile, period }) {
  const t = useT();
  const { lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState(null);
  const [err, setErr] = useState(false);

  const load = useCallback(async () => {
    if (text) return;
    setLoading(true); setErr(false);
    try {
      const { data } = await api.get(`/coach/analytics-v2/tile-explain?tile=${tile}&period=${period}&lang=${lang}`);
      if (!data?.explanation) throw new Error("empty");
      setText(String(data.explanation));
    } catch { setErr(true); }
    setLoading(false);
  }, [tile, period, lang, text]);

  const onClick = () => { if (!open) load(); setOpen((v) => !v); };

  return (
    <div className="mt-3 border-t border-border pt-2">
      <button onClick={onClick} data-testid={`an-tile-${tile}-tip`} className="flex items-center gap-1.5 text-xs font-medium text-primary hover:underline">
        <Sparkles className="h-3.5 w-3.5" /> {t("an.tip_button")}
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </button>
      {open && (
        <div className="mt-2 rounded-xl bg-secondary/50 p-3">
          {loading ? (
            <p className="text-xs text-muted-foreground">{t("an.tip_loading")}</p>
          ) : err ? (
            <p className="text-xs text-muted-foreground">{t("an.tip_error")}</p>
          ) : (
            <>
              <p className="whitespace-pre-line text-sm text-foreground">{text}</p>
              <p className="mt-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">{t("an.tip_source")}</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function InsightsRibbon({ insights }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  if (!insights || !insights.top_story) return null;
  const st = insights.top_story;
  const up = (st.delta_pct || 0) >= 0;
  const cur = st.divisor === 100 ? fmtEur(st.current) : String(st.current);
  const deltas = Array.isArray(insights.top_deltas) ? insights.top_deltas : [];
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="an-insights-ribbon"
        className={`flex w-full items-center gap-3 rounded-2xl border p-4 text-left ${up ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`}
      >
        <span className={`flex h-9 w-9 items-center justify-center rounded-full ${up ? "bg-emerald-200 text-emerald-700" : "bg-amber-200 text-amber-700"}`}>
          {up ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
        </span>
        <div className="min-w-0 flex-1">
          <p className={`text-[11px] font-semibold uppercase tracking-wide ${up ? "text-emerald-700" : "text-amber-700"}`}>{t("an.insights_kicker")}</p>
          <p className="truncate text-sm text-foreground">{st.label} {cur} · {up ? "▲" : "▼"} {Math.abs(st.delta_pct).toFixed(1)}% {t("an.insights_vs_prev")}</p>
          <p className="text-xs text-muted-foreground">{t("an.insights_tap")} ›</p>
        </div>
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="an-insights-modal">
          <DialogHeader>
            <DialogTitle>{t("an.insights_modal_title")}</DialogTitle>
            <DialogDescription>{t("an.insights_modal_sub")}</DialogDescription>
          </DialogHeader>
          {deltas.length === 0 ? (
            <EmptyLine>{t("an.insights_empty")}</EmptyLine>
          ) : (
            <div className="space-y-2">
              {deltas.map((d, i) => {
                const dup = (d.delta_pct || 0) >= 0;
                const dcur = d.divisor === 100 ? fmtEur(d.current) : String(d.current);
                const dprev = d.divisor === 100 ? fmtEur(d.previous) : String(d.previous);
                return (
                  <div key={d.key} data-testid={`an-insights-delta-${i + 1}`} className={`flex items-center gap-3 rounded-xl border p-3 ${dup ? "border-emerald-200 bg-emerald-50/60" : "border-amber-200 bg-amber-50/60"}`}>
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-background text-xs font-bold text-foreground">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground">{d.label}</p>
                      <p className="text-xs text-muted-foreground">{dcur} <span className="opacity-60">← {dprev}</span></p>
                    </div>
                    <span className={`shrink-0 text-sm font-semibold ${dup ? "text-emerald-600" : "text-amber-600"}`}>{dup ? "+" : ""}{d.delta_pct.toFixed(1)}%</span>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
